// GET /api/claws/{tenantId}/tool-usage, and the pure logic the usage page,
// the overview KPI cell and the paused banner share.

export type ToolUsageRange = "48h" | "7d" | "30d";

export type ToolUsageBucket = { t: string; byApp: Record<string, number> };

export type ToolUsageApp = {
  slug: string;
  name: string;
  total: number;
  countsTowardInstanceLimit: boolean;
};

export type InstanceLimit = {
  window: string;
  used: number;
  warnsAt: number | null;
  pausesAt: number | null;
  state: "ok" | "alert" | "block";
  resumesAround: string | null;
};

export type AppLimit = {
  slug: string;
  name: string;
  window: string;
  used: number;
  pausesAt: number | null;
  state: "ok" | "alert" | "block";
  resumesAround: string | null;
};

export type ToolUsage = {
  range: ToolUsageRange;
  bucketSeconds: number;
  buckets: ToolUsageBucket[];
  apps: ToolUsageApp[];
  instanceLimits: InstanceLimit[];
  appLimits: AppLimit[];
  enforced: { instance: boolean; perApp: boolean };
};

export const TOOL_USAGE_RANGES: ReadonlyArray<{ key: ToolUsageRange; label: string }> = [
  { key: "48h", label: "2 days" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
];

const PERIODS: Record<string, string> = {
  "12h": "last 12 hours",
  "48h": "last 2 days",
  "7d": "last 7 days",
  "30d": "last 30 days",
  "1h": "last hour",
  "24h": "last 24 hours",
};

export const windowPeriod = (window: string): string => PERIODS[window] ?? `last ${window}`;

// Colour slots for the chart: the busiest apps keep their own colour, the rest
// fold into "Other" so a ninth app never gets a generated hue.
export const MAX_CHART_SERIES = 5;
export const OTHER_SERIES = "__other__";

export type ChartSeries = { key: string; name: string; slot: number | null };

export const chartSeries = (apps: ReadonlyArray<ToolUsageApp>): ChartSeries[] => {
  const top = apps.slice(0, MAX_CHART_SERIES).map((a, i) => ({ key: a.slug, name: a.name, slot: i + 1 }));
  return apps.length > MAX_CHART_SERIES ? [...top, { key: OTHER_SERIES, name: "Other apps", slot: null }] : top;
};

// One bucket's values in series order, with everything outside the named
// series summed into "Other".
export const stackBucket = (bucket: ToolUsageBucket, series: ReadonlyArray<ChartSeries>): number[] => {
  const named = new Set(series.map((s) => s.key));
  const other = Object.entries(bucket.byApp)
    .filter(([slug]) => !named.has(slug))
    .reduce((sum, [, n]) => sum + n, 0);
  return series.map((s) => (s.key === OTHER_SERIES ? other : bucket.byApp[s.key] ?? 0));
};

// The instance-wide window closest to pausing, for the overview KPI cell.
export const tightestLimit = (usage: ToolUsage): (InstanceLimit & { pct: number }) | null => {
  const withLimit = usage.instanceLimits
    .filter((l) => l.pausesAt !== null && l.pausesAt > 0)
    .map((l) => ({ ...l, pct: Math.min(100, (l.used / (l.pausesAt as number)) * 100) }));
  if (withLimit.length === 0) return null;
  return withLimit.reduce((a, b) => (b.pct > a.pct ? b : a));
};

export type PausedState = {
  // Every connected app is paused, not just some.
  allApps: boolean;
  appNames: string[];
  // Latest expected resume across what is paused; null when unknown.
  resumesAround: string | null;
};

const latest = (times: ReadonlyArray<string | null>): string | null =>
  times.some((t) => t === null) ? null : (times as string[]).reduce((a, b) => (b > a ? b : a));

// Only enforced limits pause anything; a block that is not enforced is shown
// as a count, never as "paused".
export const pausedState = (usage: ToolUsage): PausedState | null => {
  const instance = usage.enforced.instance ? usage.instanceLimits.filter((l) => l.state === "block") : [];
  if (instance.length > 0) {
    return { allApps: true, appNames: [], resumesAround: latest(instance.map((l) => l.resumesAround)) };
  }
  const apps = usage.enforced.perApp ? usage.appLimits.filter((l) => l.state === "block") : [];
  if (apps.length === 0) return null;
  return {
    allApps: false,
    appNames: [...new Set(apps.map((a) => a.name))],
    resumesAround: latest(apps.map((a) => a.resumesAround)),
  };
};

// "Thu 23 Oct, 3:10 pm" in the viewer's zone; "today, 3:10 pm" when it is today.
export const formatResume = (iso: string, now: Date = new Date()): string => {
  const at = new Date(iso);
  const time = at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (at.toDateString() === now.toDateString()) return `today, ${time}`;
  const day = at.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
  return `${day}, ${time}`;
};

export const formatCount = (n: number): string => n.toLocaleString("en-US");
